import { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Banknote, AlertCircle, Pencil, RotateCcw, Ban } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import {
  compensacionesPintadas,
  resumenDeLoQueSeDebe,
  textoDeLaDeuda,
  type FilaCompensacion,
} from '../lib/loQueElClubDebe';
import type { Database } from '../types/database.types';
import Modal from '../components/ui/Modal';

// PR-G8.1 — Activación manual de bonos pagados fuera de Stripe (efectivo /
// transferencia / Bizum / otro). El form llama a `admin_grant_credits` (mig
// 101) que crea el crédito y registra la activación en `manual_bono_activations`.
//
// PR-G8.1.5 — edición/anulación (mig 102): cada fila permite editar metadata
// (update_manual_bono_metadata), anular con rollback del crédito
// (void_manual_bono_activation) o "anular y rehacer" atómico
// (revoke_and_recreate_manual_bono). Las filas anuladas se muestran tachadas.

type SessionPackRow = Database['public']['Tables']['session_packs']['Row'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];

type PackOption = Pick<
  SessionPackRow,
  'id' | 'name' | 'sessions_count' | 'credit_type' | 'validity_days'
>;
type AlumnoOption = Pick<ProfileRow, 'id' | 'full_name'>;

// Activación con embeds (alumno, pack, quién la activó). Doble cast vía
// `unknown` para los embeds (patrón ADR-0020).
type ActivationRow = {
  id: string;
  created_at: string;
  student_id: string;
  pack_id: string | null;
  sessions: number;
  credit_type: string;
  payment_method: string;
  reference: string | null;
  notes: string | null;
  voided_at: string | null;
  void_reason: string | null;
  replaced_by: string | null;
  student: { full_name: string } | null;
  pack: { name: string } | null;
  activator: { full_name: string } | null;
};

// Convenio del proyecto para RPCs admin: `{ ok, credit_id?, error? }`.
type RpcResult = { ok: true; [k: string]: unknown } | { ok: false; error: string };

const PAYMENT_METHODS = ['efectivo', 'transferencia', 'bizum', 'otro'] as const;
type PaymentMethod = (typeof PAYMENT_METHODS)[number];

type BonoFieldsState = {
  student_id: string;
  pack_id: string;
  payment_method: PaymentMethod;
  reference: string;
  notes: string;
};

const EMPTY_FIELDS: BonoFieldsState = {
  student_id: '',
  pack_id: '',
  payment_method: 'efectivo',
  reference: '',
  notes: '',
};

const METHOD_LABELS: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  bizum: 'Bizum',
  otro: 'Otro',
};

const CREDIT_TYPE_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
  gym: 'PF',
};

function friendlyError(code: string | undefined): string {
  if (code === 'partial_consumption_blocked') {
    return 'No se puede anular: el bono ya tiene sesiones consumidas.';
  }
  return code || 'Operación no completada';
}

function isPaymentMethod(v: string): v is PaymentMethod {
  return (PAYMENT_METHODS as readonly string[]).includes(v);
}

// UN HISTORIAL VACÍO AL LADO DE «ACTIVAR BONO» SE PAGA DOS VECES.
//
// Esta tabla es el registro de los bonos concedidos a mano: los que se pagan
// en efectivo, por transferencia o por Bizum, fuera de Stripe. Con la lectura
// tirando el `error` decía «No hay activaciones manuales todavía» —que es una
// afirmación sobre lo que se ha concedido— y el botón de conceder seguía al
// lado. El operador que viene a comprobar si ya dio ese bono lee que no, y lo
// da otra vez: dos veces las sesiones por un solo cobro.
//
// Por eso aquí no basta con avisar: si no se ha podido leer lo ya concedido,
// no se concede. Es lo mismo que hace `FamilyBlock` al ocultar «Vincular
// tutor» cuando no ha podido leer los vínculos.
const QUE = {
  activaciones: 'las activaciones manuales',
  alumnos: 'los alumnos',
  bonos: 'los bonos',
  matriculas: 'las matrículas',
  // mig 341 · las sesiones gratis que el club dio al cancelar.
  compensaciones: 'las sesiones gratis por cancelación',
} as const;

export default function BonosManualesPage() {
  const [activations, setActivations] = useState<ActivationRow[]>([]);
  // mig 341 · lo que el club debe. Las filas crudas; el recuento se DERIVA
  // (ver `loQueElClubDebe`), que es la decisión: ninguna columna contador.
  const [compensaciones, setCompensaciones] = useState<FilaCompensacion[]>([]);
  const [alumnos, setAlumnos] = useState<AlumnoOption[]>([]);
  const [packs, setPacks] = useState<PackOption[]>([]);
  // PR-J1: productos de matrícula (products.fulfillment_type='membership') para
  // dar de alta una matrícula manual desde el mismo flujo (admin_grant_credits
  // con p_is_membership=true).
  const [membershipProducts, setMembershipProducts] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState<'todos' | PaymentMethod>('todos');
  const [saving, setSaving] = useState(false);

  // Activar bono (nuevo)
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<BonoFieldsState>(EMPTY_FIELDS);
  const [formError, setFormError] = useState('');

  // Editar metadata
  const [editTarget, setEditTarget] = useState<ActivationRow | null>(null);
  const [editForm, setEditForm] = useState({
    payment_method: 'efectivo' as PaymentMethod,
    reference: '',
    notes: '',
  });
  const [editError, setEditError] = useState('');

  // Anular
  const [voidTarget, setVoidTarget] = useState<ActivationRow | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState('');

  // Anular y rehacer
  const [recreateTarget, setRecreateTarget] = useState<ActivationRow | null>(null);
  const [recreateForm, setRecreateForm] = useState<BonoFieldsState>(EMPTY_FIELDS);
  const [recreateReason, setRecreateReason] = useState('');
  const [recreateError, setRecreateError] = useState('');

  useEffect(() => {
    void loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    const { datos, fallidos: noLeidos } = await leerVarias([
      {
        que: QUE.activaciones,
        consulta: supabase
          .from('manual_bono_activations')
          .select(
            `id, created_at, student_id, pack_id, sessions, credit_type, payment_method,
           reference, notes, voided_at, void_reason, replaced_by,
           student:profiles!manual_bono_activations_student_id_fkey(full_name),
           pack:session_packs(name),
           activator:profiles!manual_bono_activations_activated_by_fkey(full_name)`
          )
          .order('created_at', { ascending: false }),
      },
      {
        que: QUE.alumnos,
        consulta: supabase
          .from('profiles')
          .select('id, full_name')
          .eq('role', 'alumno')
          .order('full_name'),
      },
      {
        que: QUE.bonos,
        consulta: supabase
          .from('session_packs')
          .select('id, name, sessions_count, credit_type, validity_days')
          .eq('active', true)
          .order('name'),
      },
      {
        que: QUE.compensaciones,
        // Solo las compensaciones: `origen_sesion_id` es su marcador. Se traen
        // TODAS —vivas, gastadas y caducadas— y la frontera la pone el helper,
        // porque la tarjeta tiene que poder decir «caducaron 3 sin usarse», que
        // es la promesa que el club no cumplió.
        consulta: supabase
          .from('user_credits')
          .select(
            'id, user_id, sessions_remaining, expiry_date, gift_reason, credit_type, alumno:profiles!user_credits_user_id_fkey(full_name)'
          )
          .not('origen_sesion_id', 'is', null)
          .order('expiry_date', { ascending: true }),
      },
      {
        que: QUE.matriculas,
        consulta: supabase
          .from('products')
          .select('id, name')
          .eq('active', true)
          .eq('fulfillment_type', 'membership')
          .order('name'),
      },
    ]);
    // ⚠️ EL ORDEN ES EL DE `leerVarias`, no el de las lecturas en la cabeza de
    // nadie: la nueva entra ANTES de las matrículas, así que `mems` se corre
    // un puesto. Equivocarlo no da error de tipos —las dos son listas— y
    // dejaría la tarjeta leyendo matrículas.
    const [acts, alus, pks, comps, mems] = datos;

    setFallidos(noLeidos);
    setActivations((acts ?? []) as unknown as ActivationRow[]);
    setAlumnos((alus ?? []) as AlumnoOption[]);
    setPacks((pks ?? []) as PackOption[]);
    setCompensaciones(
      (
        (comps ?? []) as unknown as (Omit<FilaCompensacion, 'alumno'> & {
          alumno: { full_name: string } | null;
        })[]
      ).map((c) => ({ ...c, alumno: c.alumno?.full_name ?? null }))
    );
    setMembershipProducts((mems ?? []) as { id: string; name: string }[]);
    setLoading(false);
  }

  const byId = useMemo(() => {
    const m = new Map<string, ActivationRow>();
    activations.forEach((a) => m.set(a.id, a));
    return m;
  }, [activations]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return activations.filter((a) => {
      const matchSearch =
        !q ||
        (a.student?.full_name ?? '').toLowerCase().includes(q) ||
        (a.pack?.name ?? '').toLowerCase().includes(q) ||
        (a.reference ?? '').toLowerCase().includes(q);
      const matchMethod = filtroMetodo === 'todos' || a.payment_method === filtroMetodo;
      return matchSearch && matchMethod;
    });
  }, [activations, search, filtroMetodo]);

  // Campos compartidos por los modales "activar" y "rehacer". Función (no
  // componente) para no remontar los inputs en cada tecla.
  const bonoFields = (
    state: BonoFieldsState,
    patch: (p: Partial<BonoFieldsState>) => void,
    ids: { alumno: string; pack: string; metodo: string; reference: string; notes: string },
    allowMembership = false
  ) => {
    const pack = packs.find((p) => p.id === state.pack_id) ?? null;
    const isMembership = membershipProducts.some((m) => m.id === state.pack_id);
    return (
      <>
        <div>
          <label className="label">Alumno *</label>
          <select
            className="input"
            value={state.student_id}
            onChange={(e) => patch({ student_id: e.target.value })}
            data-testid={ids.alumno}
          >
            <option value="">Selecciona un alumno…</option>
            {alumnos.map((a) => (
              <option key={a.id} value={a.id}>
                {a.full_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">{allowMembership ? 'Producto *' : 'Bono *'}</label>
          <select
            className="input"
            value={state.pack_id}
            onChange={(e) => patch({ pack_id: e.target.value })}
            data-testid={ids.pack}
          >
            <option value="">Selecciona…</option>
            <optgroup label="Bonos">
              {packs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.sessions_count} ses. ·{' '}
                  {CREDIT_TYPE_LABELS[p.credit_type] ?? p.credit_type}
                </option>
              ))}
            </optgroup>
            {allowMembership && membershipProducts.length > 0 && (
              <optgroup label="Matrícula">
                {membershipProducts.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} · Matrícula
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {isMembership ? (
            <p className="text-xs text-text-secondary mt-1">
              Matrícula · vigencia hasta el 31/07 de la próxima temporada
            </p>
          ) : (
            pack && (
              <p className="text-xs text-text-secondary mt-1">
                {pack.sessions_count} sesión(es) · caduca en {pack.validity_days ?? 180} días
              </p>
            )
          )}
        </div>

        <div>
          <label className="label">Método de pago *</label>
          <select
            className="input"
            value={state.payment_method}
            onChange={(e) =>
              patch({ payment_method: isPaymentMethod(e.target.value) ? e.target.value : 'otro' })
            }
            data-testid={ids.metodo}
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Referencia</label>
          <input
            type="text"
            className="input"
            placeholder="Nº de recibo, últimos dígitos IBAN, ID Bizum…"
            value={state.reference}
            onChange={(e) => patch({ reference: e.target.value })}
            data-testid={ids.reference}
          />
        </div>

        <div>
          <label className="label">Notas</label>
          <textarea
            className="input min-h-[72px]"
            placeholder="Observaciones internas (opcional)"
            value={state.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            data-testid={ids.notes}
          />
        </div>
      </>
    );
  };

  function openActivate() {
    setForm(EMPTY_FIELDS);
    setFormError('');
    setModalOpen(true);
  }

  function grantArgsFromFields(state: BonoFieldsState, extra: { void_reason?: string }) {
    const pack = packs.find((p) => p.id === state.pack_id);
    if (!pack) return null;
    const validityDays = pack.validity_days ?? 180;
    const expiry = new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toISOString();
    return {
      sessions: pack.sessions_count,
      credit_type: pack.credit_type,
      expiry,
      pack_id: pack.id,
      ...extra,
    };
  }

  async function handleActivate() {
    if (!form.student_id) return setFormError('Selecciona un alumno');
    const isMembership = membershipProducts.some((m) => m.id === form.pack_id);
    setSaving(true);
    setFormError('');

    // Matrícula manual → admin_grant_credits con p_is_membership=true (no crea
    // user_credits; fija profiles.membership_active_until). Bono → flujo previo.
    let resp;
    if (isMembership) {
      resp = await supabase.rpc('admin_grant_credits', {
        p_student_id: form.student_id,
        p_sessions: 0,
        p_payment_method: form.payment_method,
        p_reference: form.reference.trim() || undefined,
        p_notes: form.notes.trim() || undefined,
        p_is_membership: true,
      });
    } else {
      const derived = grantArgsFromFields(form, {});
      if (!derived) {
        setSaving(false);
        return setFormError('Selecciona un bono');
      }
      resp = await supabase.rpc('admin_grant_credits', {
        p_student_id: form.student_id,
        p_sessions: derived.sessions,
        p_credit_type: derived.credit_type,
        p_expiry_date: derived.expiry,
        p_payment_method: form.payment_method,
        p_reference: form.reference.trim() || undefined,
        p_notes: form.notes.trim() || undefined,
        p_pack_id: derived.pack_id,
      });
    }
    setSaving(false);
    const result = resp.data as RpcResult | null;
    if (resp.error || !result?.ok) {
      setFormError(friendlyError(result && !result.ok ? result.error : resp.error?.message));
      return;
    }
    setModalOpen(false);
    void loadAll();
  }

  function openEdit(a: ActivationRow) {
    setEditTarget(a);
    setEditForm({
      payment_method: isPaymentMethod(a.payment_method) ? a.payment_method : 'otro',
      reference: a.reference ?? '',
      notes: a.notes ?? '',
    });
    setEditError('');
  }

  async function handleEditSave() {
    if (!editTarget) return;
    setSaving(true);
    setEditError('');
    const { data, error } = await supabase.rpc('update_manual_bono_metadata', {
      p_activation_id: editTarget.id,
      p_payment_method: editForm.payment_method,
      p_reference: editForm.reference.trim(),
      p_notes: editForm.notes.trim(),
    });
    setSaving(false);
    const result = data as RpcResult | null;
    if (error || !result?.ok) {
      setEditError(friendlyError(result && !result.ok ? result.error : error?.message));
      return;
    }
    setEditTarget(null);
    void loadAll();
  }

  function openVoid(a: ActivationRow) {
    setVoidTarget(a);
    setVoidReason('');
    setVoidError('');
  }

  async function handleVoid() {
    if (!voidTarget) return;
    if (!voidReason.trim()) return setVoidError('La razón es obligatoria');
    setSaving(true);
    setVoidError('');
    const { data, error } = await supabase.rpc('void_manual_bono_activation', {
      p_activation_id: voidTarget.id,
      p_void_reason: voidReason.trim(),
    });
    setSaving(false);
    const result = data as RpcResult | null;
    if (error || !result?.ok) {
      setVoidError(friendlyError(result && !result.ok ? result.error : error?.message));
      return;
    }
    setVoidTarget(null);
    void loadAll();
  }

  function openRecreate(a: ActivationRow) {
    setRecreateTarget(a);
    setRecreateForm({
      student_id: a.student_id,
      pack_id: a.pack_id ?? '',
      payment_method: isPaymentMethod(a.payment_method) ? a.payment_method : 'otro',
      reference: a.reference ?? '',
      notes: a.notes ?? '',
    });
    setRecreateReason('');
    setRecreateError('');
  }

  async function handleRecreate() {
    if (!recreateTarget) return;
    if (!recreateReason.trim()) return setRecreateError('La razón de anulación es obligatoria');
    if (!recreateForm.student_id) return setRecreateError('Selecciona un alumno');
    const derived = grantArgsFromFields(recreateForm, {});
    if (!derived) return setRecreateError('Selecciona un bono');
    setSaving(true);
    setRecreateError('');

    const { data, error } = await supabase.rpc('revoke_and_recreate_manual_bono', {
      p_activation_id: recreateTarget.id,
      p_void_reason: recreateReason.trim(),
      p_student_id: recreateForm.student_id,
      p_sessions: derived.sessions,
      p_credit_type: derived.credit_type,
      p_expiry_date: derived.expiry,
      p_payment_method: recreateForm.payment_method,
      p_reference: recreateForm.reference.trim() || undefined,
      p_notes: recreateForm.notes.trim() || undefined,
      p_pack_id: derived.pack_id,
    });
    setSaving(false);
    const result = data as RpcResult | null;
    if (error || !result?.ok) {
      setRecreateError(friendlyError(result && !result.ok ? result.error : error?.message));
      return;
    }
    setRecreateTarget(null);
    void loadAll();
  }

  const noSePuedeConceder = fallidos.length > 0;

  return (
    <div className="space-y-6">
      {noSePuedeConceder && (
        <div
          className="text-error text-sm font-inter bg-error/10 border border-error/30 rounded-lg px-4 py-3 flex items-start gap-2"
          data-testid="bonos-lecturas-fallidas"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            No se han podido leer {listaEnCastellano(fallidos)}. Activar un bono sin ver lo que ya
            está concedido puede duplicarlo, así que el botón queda desactivado hasta que la
            consulta responda.
          </span>
        </div>
      )}

      {/* mig 341 · LO QUE EL CLUB DEBE.
          Aquí y no en otra pantalla porque esta es la de los bonos concedidos
          a mano: una sesión gratis por una cancelación es exactamente eso, un
          bono que el club dio sin cobrar.

          ⚠️ El recuento se DERIVA de las filas (`resumenDeLoQueSeDebe`), no de
          una columna. La mig 320 existió porque había dos contadores de lo
          mismo y derivaron. */}
      {!fallidos.includes(QUE.compensaciones) && (
        <div className="card" data-testid="bonos-lo-que-el-club-debe">
          <div className="flex items-center gap-2 mb-2">
            <Banknote size={15} className="text-gold-primary" />
            <h2 className="font-oswald text-white uppercase tracking-wider text-sm">
              Sesiones gratis por cancelación
            </h2>
          </div>
          <p className="text-text-secondary text-sm font-inter" data-testid="bonos-deuda-texto">
            {textoDeLaDeuda(resumenDeLoQueSeDebe(compensaciones, new Date()))}
            {resumenDeLoQueSeDebe(compensaciones, new Date()).caducanPronto > 0 && (
              <span className="text-warning">
                {' '}
                {resumenDeLoQueSeDebe(compensaciones, new Date()).caducanPronto} caduca(n) en 7 días
                o menos.
              </span>
            )}
            {resumenDeLoQueSeDebe(compensaciones, new Date()).caducadasSinUsar > 0 && (
              <span className="text-error">
                {' '}
                Y {resumenDeLoQueSeDebe(compensaciones, new Date()).caducadasSinUsar} caducaron sin
                usarse.
              </span>
            )}
          </p>
          {compensaciones.length > 0 && (
            <table className="w-full text-sm font-inter mt-3">
              <tbody>
                {compensacionesPintadas(compensaciones, new Date()).map((c) => (
                  <tr key={c.id} className={c.viva ? '' : 'opacity-50 line-through'}>
                    <td className="py-1 text-white">{c.alumno ?? '(no disponible)'}</td>
                    <td className="py-1 text-text-secondary">{c.gift_reason}</td>
                    <td className="py-1 text-text-secondary text-right">
                      {c.diasRestantes === null
                        ? 'sin caducidad'
                        : c.viva
                          ? `caduca en ${c.diasRestantes} día(s)`
                          : 'caducada'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Barra de acciones */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {(['todos', ...PAYMENT_METHODS] as const).map((m) => (
            <button
              key={m}
              onClick={() => setFiltroMetodo(m)}
              className={
                filtroMetodo === m
                  ? 'btn-gold text-xs px-3 py-1.5'
                  : 'btn-ghost text-xs px-3 py-1.5'
              }
            >
              {m === 'todos' ? 'Todos' : METHOD_LABELS[m]}
            </button>
          ))}
        </div>

        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              className="input pl-9 h-9 text-sm"
              placeholder="Buscar por alumno, bono o referencia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={openActivate}
            disabled={noSePuedeConceder}
            title={
              noSePuedeConceder
                ? 'No se ha podido leer lo ya concedido: recarga antes de activar un bono.'
                : undefined
            }
            className="btn-gold flex items-center gap-2 h-9 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="open-activar-bono"
          >
            <Plus size={16} />
            <span className="text-sm">Activar bono</span>
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Fecha</th>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Bono</th>
                  <th className="table-th text-center">Sesiones</th>
                  <th className="table-th">Método</th>
                  <th className="table-th">Referencia</th>
                  <th className="table-th text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="table-td text-center text-text-secondary py-12"
                      data-testid="bonos-tabla-vacia"
                    >
                      {fallidos.includes(QUE.activaciones)
                        ? 'No se han podido leer las activaciones manuales.'
                        : 'No hay activaciones manuales todavía'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((a) => {
                    const voided = a.voided_at !== null;
                    const replacement = a.replaced_by ? byId.get(a.replaced_by) : undefined;
                    const cell = voided ? 'table-td line-through text-text-secondary' : 'table-td';
                    return (
                      <tr
                        key={a.id}
                        className={voided ? 'table-row opacity-60' : 'table-row'}
                        data-testid={`activation-row-${a.id}`}
                      >
                        <td className={`${cell} whitespace-nowrap`}>
                          {format(new Date(a.created_at), 'dd/MM/yyyy')}
                        </td>
                        <td className={`${cell} font-medium`}>{a.student?.full_name ?? '—'}</td>
                        <td className={cell}>{a.pack?.name ?? '—'}</td>
                        <td className={`${cell} text-center`}>{a.sessions}</td>
                        <td className={cell}>
                          <span className="inline-flex items-center gap-1.5">
                            <Banknote size={14} className="text-gold-primary" />
                            {METHOD_LABELS[a.payment_method] ?? a.payment_method}
                          </span>
                        </td>
                        <td className={cell}>{a.reference || '—'}</td>
                        <td className="table-td text-right">
                          {voided ? (
                            <div className="inline-flex items-center gap-2 justify-end">
                              <span
                                className="px-2 py-0.5 rounded-full text-xs bg-error/15 text-error"
                                title={a.void_reason ?? undefined}
                                data-testid={`voided-badge-${a.id}`}
                              >
                                Anulado
                              </span>
                              {replacement && (
                                <span
                                  className="text-xs text-text-secondary"
                                  title={`Rehecho el ${format(new Date(replacement.created_at), 'dd/MM/yyyy')}`}
                                >
                                  ↳ rehecho
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1 justify-end">
                              <button
                                className="btn-ghost px-2 py-1"
                                title="Editar metadata"
                                onClick={() => openEdit(a)}
                                data-testid={`edit-${a.id}`}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="btn-ghost px-2 py-1"
                                title="Anular y rehacer"
                                onClick={() => openRecreate(a)}
                                data-testid={`recreate-${a.id}`}
                              >
                                <RotateCcw size={14} />
                              </button>
                              <button
                                className="btn-ghost px-2 py-1 text-error"
                                title="Anular"
                                onClick={() => openVoid(a)}
                                data-testid={`void-${a.id}`}
                              >
                                <Ban size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal activar bono */}
      <Modal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title="Activar bono o matrícula"
      >
        <div className="space-y-4">
          {bonoFields(
            form,
            (p) => setForm((f) => ({ ...f, ...p })),
            {
              alumno: 'select-alumno',
              pack: 'select-pack',
              metodo: 'select-metodo',
              reference: 'input-reference',
              notes: 'input-notes',
            },
            true
          )}
          {formError && (
            <p className="flex items-center gap-2 text-error text-sm">
              <AlertCircle size={16} />
              {formError}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </button>
            <button
              className="btn-gold"
              onClick={handleActivate}
              disabled={saving}
              data-testid="submit-activar-bono"
            >
              {saving ? 'Activando…' : 'Activar bono'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal editar metadata */}
      <Modal
        open={editTarget !== null}
        onClose={() => !saving && setEditTarget(null)}
        title="Editar metadata"
      >
        <div className="space-y-4">
          <p className="text-xs text-text-secondary">
            Solo cambia método / referencia / notas. No afecta al saldo del bono.
          </p>
          <div>
            <label className="label">Método de pago *</label>
            <select
              className="input"
              value={editForm.payment_method}
              onChange={(e) =>
                setEditForm((f) => ({
                  ...f,
                  payment_method: isPaymentMethod(e.target.value) ? e.target.value : 'otro',
                }))
              }
              data-testid="edit-metodo"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Referencia</label>
            <input
              type="text"
              className="input"
              value={editForm.reference}
              onChange={(e) => setEditForm((f) => ({ ...f, reference: e.target.value }))}
              data-testid="edit-reference"
            />
          </div>
          <div>
            <label className="label">Notas</label>
            <textarea
              className="input min-h-[72px]"
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="edit-notes"
            />
          </div>
          {editError && (
            <p className="flex items-center gap-2 text-error text-sm">
              <AlertCircle size={16} />
              {editError}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setEditTarget(null)} disabled={saving}>
              Cancelar
            </button>
            <button
              className="btn-gold"
              onClick={handleEditSave}
              disabled={saving}
              data-testid="submit-edit"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal anular */}
      <Modal
        open={voidTarget !== null}
        onClose={() => !saving && setVoidTarget(null)}
        title="Anular bono"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Se revertirá el crédito concedido a{' '}
            <span className="text-white font-medium">{voidTarget?.student?.full_name ?? ''}</span>.
            Solo es posible si el bono no tiene sesiones consumidas.
          </p>
          <div>
            <label className="label">Razón de la anulación *</label>
            <textarea
              className="input min-h-[72px]"
              placeholder="Ej.: alumno equivocado, pack incorrecto…"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              data-testid="void-reason"
            />
          </div>
          {voidError && (
            <p className="flex items-center gap-2 text-error text-sm">
              <AlertCircle size={16} />
              {voidError}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setVoidTarget(null)} disabled={saving}>
              Cancelar
            </button>
            <button
              className="btn-gold"
              onClick={handleVoid}
              disabled={saving}
              data-testid="submit-void"
            >
              {saving ? 'Anulando…' : 'Anular bono'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal anular y rehacer */}
      <Modal
        open={recreateTarget !== null}
        onClose={() => !saving && setRecreateTarget(null)}
        title="Anular y rehacer"
      >
        <div className="space-y-4">
          <p className="text-xs text-text-secondary">
            Anula la activación original y crea una nueva con los datos corregidos, de forma
            atómica.
          </p>
          {bonoFields(recreateForm, (p) => setRecreateForm((f) => ({ ...f, ...p })), {
            alumno: 'recreate-alumno',
            pack: 'recreate-pack',
            metodo: 'recreate-metodo',
            reference: 'recreate-reference',
            notes: 'recreate-notes',
          })}
          <div>
            <label className="label">Razón de la anulación *</label>
            <textarea
              className="input min-h-[60px]"
              placeholder="Ej.: alumno equivocado, pack incorrecto…"
              value={recreateReason}
              onChange={(e) => setRecreateReason(e.target.value)}
              data-testid="recreate-reason"
            />
          </div>
          {recreateError && (
            <p className="flex items-center gap-2 text-error text-sm">
              <AlertCircle size={16} />
              {recreateError}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setRecreateTarget(null)} disabled={saving}>
              Cancelar
            </button>
            <button
              className="btn-gold"
              onClick={handleRecreate}
              disabled={saving}
              data-testid="submit-recreate"
            >
              {saving ? 'Procesando…' : 'Anular y rehacer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
